import React from "react";
import {
  colors,
  pageHeaderStyles,
} from "../AgenticPaymentAirdrop/styles";

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatStuckSince = (minutes) => {
  const total = Number(minutes);
  if (!Number.isFinite(total) || total < 0) return "N/A";
  const rounded = Math.round(total);
  if (rounded < 60) return `${rounded}m`;
  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
};

const shorten = (value, head = 8, tail = 6) => {
  if (!value) return "";
  const str = String(value);
  if (str.length <= head + tail + 1) return str;
  return `${str.slice(0, head)}…${str.slice(-tail)}`;
};

const copyToClipboard = (text) => {
  if (!text) return;
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(String(text));
  } else {
    const ta = document.createElement("textarea");
    ta.value = String(text);
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  }
};

const th = {
  textAlign: "left",
  padding: "12px 14px",
  borderBottom: `2px solid ${colors.border}`,
  fontSize: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: colors.gray600,
  whiteSpace: "nowrap",
};

const td = {
  padding: "12px 14px",
  borderBottom: `1px solid ${colors.gray200 || "#eef0f4"}`,
  fontSize: "13px",
  color: colors.gray700,
  verticalAlign: "top",
};

const StuckProvisions = ({
  provisions = [],
  minutes = null,
  pageUrl = "/admin/stuck_provisions",
  errorMessage = null,
}) => {
  const thresholdLabel = minutes ? `${minutes} min` : "10 min (default)";

  const stuckOf = (p) => Number(p.stuckSinceMinutes ?? p.stuck_since_minutes) || 0;
  const sorted = [...provisions].sort((a, b) => stuckOf(b) - stuckOf(a));

  return (
    <div className="stuck-provisions" style={{ paddingBottom: "24px" }}>
      <div style={{ ...pageHeaderStyles.container, marginBottom: "20px", borderRadius: "12px" }}>
        <h1 style={pageHeaderStyles.title}>Stuck Provisions</h1>
        <div style={{ ...pageHeaderStyles.infoBox, flexWrap: "wrap" }}>
          <span style={pageHeaderStyles.infoLabel}>Threshold</span>
          <code style={pageHeaderStyles.infoValue}>{thresholdLabel}</code>
          <span style={pageHeaderStyles.infoLabel}>Stuck now</span>
          <code style={pageHeaderStyles.infoValue}>{provisions.length}</code>
        </div>
      </div>

      <p style={{ color: colors.gray600, fontSize: "13px", margin: "0 0 16px" }}>
        Genesis checkouts whose fiat settled but whose provision (mint / activate) leg stalled.
        These are invisible to the user until they complain, retry the provision once the
        underlying cause (mirror lag, operator balance, crypto-api) is cleared.
      </p>

      {errorMessage ? (
        <div
          style={{
            background: "#f8d7da",
            color: "#721c24",
            border: "1px solid #f5c6cb",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "16px",
            fontSize: "14px",
          }}
        >
          {errorMessage}
        </div>
      ) : null}

      {provisions.length === 0 && !errorMessage ? (
        <div
          style={{
            background: colors.white,
            border: `1px solid ${colors.border}`,
            borderRadius: "12px",
            padding: "48px 24px",
            textAlign: "center",
            color: colors.gray600,
            fontSize: "18px",
            fontWeight: 600,
          }}
        >
          No stuck provisions 🎉
        </div>
      ) : (
        <div
          style={{
            background: colors.white,
            border: `1px solid ${colors.border}`,
            borderRadius: "12px",
            overflowX: "auto",
            boxShadow: "0 10px 24px rgba(46, 59, 97, 0.08)",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
            <thead>
              <tr>
                <th style={th}>Vanity Name</th>
                <th style={th}>User</th>
                <th style={th}>Type</th>
                <th style={th}>Status</th>
                <th style={th}>Last Error</th>
                <th style={th}>Stuck Since</th>
                <th style={th}>Mint Tx</th>
                <th style={th}>Created</th>
                <th style={th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => {
                const checkoutId = p.checkoutId || p.checkout_id || p.id;
                const mintTx = p.mintTxHash || p.mint_tx_hash;
                return (
                  <tr key={checkoutId}>
                    <td style={{ ...td, fontWeight: 600, color: colors.primary }}>
                      {p.vanityName || p.vanity_name || "-"}
                    </td>
                    <td style={{ ...td, fontFamily: "monospace", fontSize: "12px" }}>
                      {shorten(p.userId || p.user_id)}
                    </td>
                    <td style={td}>{p.type || "-"}</td>
                    <td style={td}>
                      <span
                        style={{
                          padding: "3px 10px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: 600,
                          background: "#e8daef",
                          color: "#6c3483",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.status || "unknown"}
                      </span>
                    </td>
                    <td style={{ ...td, maxWidth: "280px", color: "#932419" }}>
                      {p.lastError || p.last_error || "-"}
                    </td>
                    <td style={{ ...td, fontWeight: 600, whiteSpace: "nowrap" }}>
                      {formatStuckSince(p.stuckSinceMinutes ?? p.stuck_since_minutes)}
                    </td>
                    <td style={{ ...td, fontFamily: "monospace", fontSize: "12px" }}>
                      {mintTx ? (
                        <a
                          href={`https://hashscan.io/mainnet/transaction/${mintTx}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: colors.accent || "#e58734" }}
                        >
                          {shorten(mintTx)}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      {formatDateTime(p.createdAt || p.created_at)}
                    </td>
                    <td style={td}>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(checkoutId)}
                        title={`Copy checkout ID: ${checkoutId}`}
                        style={{
                          background: colors.white,
                          color: colors.primary,
                          border: `1px solid ${colors.border}`,
                          borderRadius: "6px",
                          padding: "6px 12px",
                          fontSize: "12px",
                          fontWeight: 600,
                          lineHeight: 1.2,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Copy checkout ID
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StuckProvisions;
